import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { searchInvoices } from '../../api/admin/endpoints';
import type { InvoiceSortKey } from '../../api/admin/types';
import type { InvoiceDocumentType, InvoiceStatus } from '../../api/shared';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate, formatMoney } from '../../components/format';
import { InvoiceStatusChip } from '../../components/InvoiceStatusChip';
import { PageHeader } from '../../components/PageHeader';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

const DOCUMENT_TYPES: Array<InvoiceDocumentType> = ['PROFORMA', 'INVOICE'];
const STATUSES: Array<InvoiceStatus> = ['ISSUED', 'PARTIALLY_PAID', 'PAID', 'CANCELLED'];

/**
 * Every licence document of the platform, server-paginated and server-sorted (Conventions/08 §3).
 * `?tenantId=…&tenantName=…` narrows it to one business — the tenant detail page links here.
 */
export const InvoicesPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? undefined;
  const tenantName = searchParams.get('tenantName') ?? '';
  const [term, setTerm] = useState('');
  const [documentType, setDocumentType] = useState<InvoiceDocumentType | ''>('');
  const [status, setStatus] = useState<InvoiceStatus | ''>('');
  const [overdue, setOverdue] = useState(false);
  const [page, setPage] = useState(0);
  const sort = useSort<InvoiceSortKey>('issuedOn', 'desc', () => setPage(0));
  const debouncedTerm = useDebouncedValue(term);

  const query = useQuery({
    queryKey: [
      'invoices',
      tenantId,
      debouncedTerm,
      documentType,
      status,
      overdue,
      page,
      sort.key,
      sort.direction,
    ],
    queryFn: () =>
      searchInvoices({
        tenantId,
        term: debouncedTerm,
        documentType: documentType || undefined,
        status: status || undefined,
        overdue,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t('invoices.title')}
        subtitle={tenantId ? t('invoices.tenantFilter', { name: tenantName }) : t('invoices.subtitle')}
        action={
          tenantId ? (
            <Button
              onClick={() => {
                setSearchParams({});
                setPage(0);
              }}
            >
              {t('invoices.clearTenant')}
            </Button>
          ) : undefined
        }
      />
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('invoices.searchHint')}
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label={t('field.documentType')}
            value={documentType}
            onChange={(event) => {
              setDocumentType(event.target.value as InvoiceDocumentType | '');
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {DOCUMENT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {t(`documentType.${type}` as const)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('field.status')}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as InvoiceStatus | '');
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`invoiceStatus.${value}` as const)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={overdue}
                onChange={(event) => {
                  setOverdue(event.target.checked);
                  setPage(0);
                }}
              />
            }
            label={t('invoices.overdueOnly')}
          />
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="number">
                  {t('field.number')}
                </SortableCell>
                <TableCell>{t('field.documentType')}</TableCell>
                <SortableCell sort={sort} column="buyer">
                  {t('field.buyer')}
                </SortableCell>
                <SortableCell sort={sort} column="issuedOn">
                  {t('field.issuedOn')}
                </SortableCell>
                <SortableCell sort={sort} column="dueOn">
                  {t('field.dueOn')}
                </SortableCell>
                <SortableCell sort={sort} column="totalMinor" align="right">
                  {t('field.total')}
                </SortableCell>
                <SortableCell sort={sort} column="status">
                  {t('field.status')}
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading ? (
                <TableSkeletonRows columns={7} />
              ) : (
                rows.map((invoice) => (
                  <TableRow
                    key={invoice.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/platform/invoices/${invoice.id}`)}
                  >
                    <TableCell sx={{ fontWeight: 560 }}>{invoice.number}</TableCell>
                    <TableCell>{t(`documentType.${invoice.documentType}` as const)}</TableCell>
                    <TableCell>{invoice.buyerName}</TableCell>
                    <TableCell>{formatDate(invoice.issuedOn, language)}</TableCell>
                    <TableCell>{formatDate(invoice.dueOn, language)}</TableCell>
                    <TableCell align="right">
                      {formatMoney(invoice.totalMinor, invoice.currency, language)}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatusChip status={invoice.status} overdue={invoice.overdue} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && rows.length === 0 ? (
          <EmptyState title={t('invoices.empty')} detail={t('invoices.emptyDetail')} />
        ) : null}
        <ServerPagination
          page={page}
          pageSize={query.data?.pageSize ?? 25}
          total={query.data?.total ?? 0}
          onPageChange={setPage}
        />
      </Paper>
    </>
  );
};
